import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import EventListener from 'react-event-listener';
import {connect} from 'react-redux';

import interaction from 'topmeteo/flight-planning/utils/interaction';

import {useMap} from './OpenLayers/hooks';

import useDrawing, {LengthContext, DrawingContext} from './Drawing';
import useFAITriangle from './FAITriangle';
import useSector from './Sector';

const Interaction = ({zIndex, FAIEnabled, children}) => {
    const faiVectorSource = useFAITriangle(zIndex);
    const drawing = useDrawing(zIndex);
    const sector = useSector(zIndex);

    const [length, setLength] = useState(null);
    const [FAILength, setFAILength] = useState(null);
    const [isDrawing, setIsDrawing] = useState(null);

    const isFAIEnabled = useRef(FAIEnabled);
    const startDrawing = useRef(null);
    const stopDrawing = useRef(null);
    const handleEscape = useRef(null);
    const waypoints = useRef(null);

    useEffect(() => { isFAIEnabled.current = FAIEnabled; }, [FAIEnabled]);

    const handleStartDrawing = useCallback(
        () => {
            setIsDrawing(true);
            startDrawing.current();
        },
        [setIsDrawing, startDrawing],
    );

    const handleFinishDrawing = useCallback(() => setIsDrawing(false), [setIsDrawing]);

    useMap(
        map => {
            const handlers = interaction(
                map,
                drawing,
                sector,
                faiVectorSource,
                () => isFAIEnabled.current,
                setLength,
                setFAILength,
                handleFinishDrawing,
                value => { waypoints.current = value; },
            );

            handleEscape.current = handlers.handleEscape;
            startDrawing.current = handlers.startDrawing;
            stopDrawing.current = handlers.stopDrawing;

            setLength(0);
            setFAILength(0);
            setIsDrawing(false);
        },
        [faiVectorSource, drawing, sector, isFAIEnabled, handleFinishDrawing],
    );

    const lengthContextValue = useMemo(
        () => ({
            length,
            FAILength,
        }),
        [length, FAILength],
    );

    const drawingContextValue = useMemo(
        () => ({
            waypoints,
            isDrawing,
            startDrawing: handleStartDrawing,
            finishDrawing: stopDrawing.current,
        }),
        [waypoints, isDrawing, handleStartDrawing, stopDrawing],
    );

    const events = {};

    if (handleEscape.current) events.onKeyPress = handleEscape.current;

    return (
        <EventListener target={document} {...events}>
            <LengthContext.Provider value={lengthContextValue}>
                <DrawingContext.Provider value={drawingContextValue}>
                    {children}
                </DrawingContext.Provider>
            </LengthContext.Provider>
        </EventListener>
    );
};

const mapStateToProps = ({FAIEnabled}) => ({FAIEnabled});

export default connect(mapStateToProps)(Interaction);
